/**
 * Right rail — per-edit logic results (✓/✗), actuals summary, and the staged
 * recovery edits (each unstageable with ✕). Constraint violations are flagged
 * inline on each row, not here.
 */
import React from "react";
import type { TaktColorEntry, WorkbenchResult } from "../../../../../models";
import type { RecoveryDesc } from "../CascadeWorkbenchModal";
import { formatDateMonthDay, statusLabel } from "../../../../../utils/format";
import "./WorkbenchRail.css";

interface Props {
  result: WorkbenchResult;
  nameById: Map<number, string>;
  activityWorkCodeMap: Map<number, string>;
  colors: Record<string, TaktColorEntry>;
  recovery: Array<[string, RecoveryDesc]>;
  onUnstage?: (key: string) => void;
}

const WorkbenchRail: React.FC<Props> = ({
  result,
  nameById,
  activityWorkCodeMap,
  colors,
  recovery,
  onUnstage,
}) => {
  const endpoint = (id: number | null) => {
    if (id == null) return null;
    const code = activityWorkCodeMap.get(id);
    const fill = (code ? colors[code]?.fill : undefined) || "#666";
    return { fill, name: nameById.get(id) ?? `#${id}` };
  };

  return (
    <aside className="cwb-rail">
      {result.error && (
        <div className="cwb-rail-section">
          <div className="cwb-rail-title">Error</div>
          <div className="cwb-rail-error-msg">{result.error}</div>
        </div>
      )}

      {recovery.length > 0 && (
        <div className="cwb-rail-section">
          <div className="cwb-rail-title">Recovery edits ({recovery.length})</div>
          {recovery.map(([key, d]) => (
            <div key={key} className="cwb-card cwb-card--recovery">
              <div className="cwb-rail-recovery-line">
                <span className="cwb-card-kind">
                  {d.kind === "dur" ? "Duration" : "Dependency"}
                </span>
                {onUnstage && (
                  <button
                    type="button"
                    className="cwb-rail-unstage"
                    title="Remove this recovery edit"
                    onClick={() => onUnstage(key)}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="cwb-card-label" title={d.label}>{d.label}</div>
              <div className="cwb-change-line">
                <span className="cwb-was">{d.was}</span>
                <span className="cwb-now">{d.now}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {result.logic_results.length > 0 && (
        <div className="cwb-rail-section">
          <div className="cwb-rail-title">
            Logic changes ({result.logic_results.length})
          </div>
          {result.logic_results.map((r, i) => {
            const verb =
              r.op === "add" ? "Add pred" : r.op === "remove" ? "Remove pred" : "Update pred";
            const pred = endpoint(r.pred_id);
            const succ = endpoint(r.succ_id);
            return (
              <div
                key={i}
                className={`cwb-card ${r.error ? "cwb-card--error" : "cwb-card--ok"}`}
              >
                <div className="cwb-rail-item-line">
                  <span className="cwb-rail-mark">{r.error ? "✗" : "✓"}</span>
                  <span className="cwb-rail-verb">{verb}</span>
                </div>
                <div className="cwb-rail-edge">
                  {pred && (
                    <div className="cwb-rail-endpoint">
                      <span className="cwb-rail-swatch" style={{ background: pred.fill }} />
                      <span className="cwb-rail-endpoint-name">{pred.name}</span>
                    </div>
                  )}
                  {pred && succ && <span className="cwb-rail-edge-arrow">↓</span>}
                  {succ && (
                    <div className="cwb-rail-endpoint">
                      <span className="cwb-rail-swatch" style={{ background: succ.fill }} />
                      <span className="cwb-rail-endpoint-name">{succ.name}</span>
                    </div>
                  )}
                </div>
                {r.error && <div className="cwb-rail-error-msg">{r.error}</div>}
              </div>
            );
          })}
        </div>
      )}

      {result.actuals_summary && (
        <div className="cwb-rail-section">
          <div className="cwb-rail-title">Actuals</div>
          {result.actuals_summary.actual_start && (
            <div className="cwb-card">
              <span className="cwb-rail-label">Actual Start</span>
              <span>{formatDateMonthDay(result.actuals_summary.actual_start)}</span>
            </div>
          )}
          {result.actuals_summary.actual_finish && (
            <div className="cwb-card">
              <span className="cwb-rail-label">Actual Finish</span>
              <span>{formatDateMonthDay(result.actuals_summary.actual_finish)}</span>
            </div>
          )}
          <div className="cwb-card">
            <span className="cwb-rail-label">Status</span>
            <span>{statusLabel(result.actuals_summary.new_status)}</span>
          </div>
        </div>
      )}

    </aside>
  );
};

export default WorkbenchRail;
